import React, { useState, useEffect } from "react";
import axios from "axios";
import { WEBSERVICE } from "../../CoreDataService";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Modal,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Box,
  Grid,
} from "@mui/material";
import { useSelector } from "react-redux";
import DataTable from "../../Common/DataTable";
import { ContainedButton } from "../../Common/Buttons";

const FeeModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [userExchangeFee, setUserExchangeFee] = useState({
    totalVolume: 0,
    totalFees: 0,
    makerFee: 0,
    takerFee: 0,
  });
  const [exchangeFees, setExchangeFees] = useState([]);
  const [error, setError] = useState(null);
  const { brokerBasicInfo, brokerDetailedInfo, btncolor } = useSelector(
    (state) => state
  );

  useEffect(() => {
    if (open) {
      fetchFees();
    }
  }, [open]);

  const fetchFees = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch volume-wise trading fees
      const { data: tradingFees } = await axios.get(
        WEBSERVICE + "/userTrade/volumeWiseTradingFees",
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      // Fetch user-specific trading fees
      const { data: userFees } = await axios.get(
        WEBSERVICE +
          `/userTrade/userVolumeWiseTradingFees?uuid=${localStorage.getItem(
            "uuid"
          )}`,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );

      setUserExchangeFee({
        totalVolume: parseFloat(
          userFees.userTradingFees[0].totalVolume
        ).toFixed(2),
        totalFees: parseFloat(userFees.userTradingFees[0].totalFees).toFixed(2),
        makerFee: userFees.userTradingFees[0].makerFee,
        takerFee: userFees.userTradingFees[0].takerFee,
      });

      setExchangeFees(tradingFees.value);
    } catch (error) {
      setError("Could not fetch the data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ textAlign: "center" }}>
        Fees
        <Button
          onClick={onClose}
          style={{ float: "right" }}
          // className="allCommonButton"
          variant="text"
        >
          &times;
        </Button>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="h6" gutterBottom>
          Summary - 30 Days
        </Typography>

        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <DataTable
              columns={[
                "Total Volume",
                "Total Fees Accrued",
                "Current Maker Fees",
                "Current Taker Fees",
              ]}
            >
              <TableRow>
                <TableCell>${userExchangeFee.totalVolume}</TableCell>
                <TableCell>${userExchangeFee.totalFees}</TableCell>
                <TableCell>{userExchangeFee.makerFee}%</TableCell>
                <TableCell>{userExchangeFee.takerFee}%</TableCell>
              </TableRow>
            </DataTable>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Your fee tier is based upon total USD trading volume over the
              trailing 30 day period. Total Volume includes volume for trading
              Pairs in CONVERT, SPOT and OTC trading only. Transactions made on
              books quoted in USD, e.g. BTC-USD, are counted as the total USD
              amount of each filled order. Transactions made on non-USD books
              are converted to USD based on the most recent fill price on the
              respective book.
            </Typography>

            <Typography variant="h6" gutterBottom>
              Exchange Fees
            </Typography>

            <DataTable
              columns={["Tier (30 Day Trailing)", "Maker Fee", "Taker Fee"]}
            >
              {exchangeFees.length > 0 ? (
                exchangeFees.map((fee, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {fee.volumeFrom} - {fee.volumeTo}
                    </TableCell>
                    <TableCell>{fee.makerFee}</TableCell>
                    <TableCell>{fee.takerFee}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No exchange fees data available.
                  </TableCell>
                </TableRow>
              )}
            </DataTable>

            <Typography variant="body2" color="textSecondary">
              {brokerBasicInfo?.exchange} uses a maker-taker fee model for
              determining its trading fees. Orders that provide liquidity (maker
              orders) are charged different fees than orders that take liquidity
              (taker orders).
              <b>
                Fees are calculated based on the current pricing tier you are in
                when the order is placed, and not on the tier you would be in
                after a trade is completed.
              </b>{" "}
              When you place an order at the market price that gets filled
              immediately, you are considered a taker. When you place an order
              which is not immediately matched by an existing order, that order
              is placed on the order book. If another customer places an order
              that matches yours, you are considered the maker. When you place
              an order that gets partially matched immediately, you pay a taker
              fee for that portion. The remainder of the order is placed on the
              order book and, when matched, is considered a maker order.
            </Typography>
          </>
        )}

        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center" }}>
        <ContainedButton onClick={onClose} variant="contained" size="small">
          Close
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
};

export default FeeModal;
